import {
  useLayoutEffect, useCallback, useState, useMemo,
} from 'react';

const ARROW_BORDER = '30px solid white';
const ARROW_WIDTH = 30;
const ARROW_HEIGHT = 45;

const useAnchoredModal = ({ align, padding = 45, isMobile }) => {
  const [element, setElement] = useState(null);
  const [dims, setDims] = useState(null);

  const anchorRef = useCallback((node) => {
    setElement(node);
  }, []);

  const measureAndUpdate = useCallback(() => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    setDims((prev) => {
      if (
        prev
        && prev.width === rect.width
        && prev.top === rect.top
        && prev.right === rect.right
        && prev.bottom === rect.bottom
        && prev.left === rect.left
      ) {
        return prev;
      }
      return {
        width: rect.width,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
      };
    });
  }, [element]);

  useLayoutEffect(() => {
    if (!element) return undefined;

    let rafId = requestAnimationFrame(() => {
      measureAndUpdate();
    });

    // Defer to next frame to avoid
    // "ResizeObserver loop completed with undelivered notifications" error
    const resizeObserver = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        measureAndUpdate();
      });
    });

    resizeObserver.observe(element);

    window.addEventListener('resize', measureAndUpdate);
    window.addEventListener('scroll', measureAndUpdate, true);
    window.addEventListener('transitionend', measureAndUpdate, true);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureAndUpdate);
      window.removeEventListener('scroll', measureAndUpdate, true);
      window.removeEventListener('transitionend', measureAndUpdate, true);
    };
  }, [element, measureAndUpdate]);

  const { modalStyle, arrowStyle } = useMemo(() => {
    const rawModalStyle = {};
    const rawArrowStyle = {};

    if (isMobile || !align || !dims) {
      return { modalStyle: rawModalStyle, arrowStyle: rawArrowStyle };
    }

    // getBoundingClientRect returns viewport-relative coords,
    // so the modal must use fixed positioning to match.
    rawModalStyle.position = 'fixed';

    const {
      width, top, right, bottom, left,
    } = dims;

    const anchorCenterY = top + ((bottom - top) / 2);
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const hasVerticalAlign = align.includes('top') || align.includes('bottom');

    align.forEach((direction) => {
      switch (direction) {
        // Anchor is above → modal below
        case 'top':
          rawModalStyle.top = bottom + padding;
          rawArrowStyle.borderBottom = ARROW_BORDER;
          rawArrowStyle.top = -ARROW_HEIGHT;
          break;
        // Anchor is below → modal above
        case 'bottom':
          rawModalStyle.bottom = windowHeight - top + padding;
          rawArrowStyle.borderTop = ARROW_BORDER;
          rawArrowStyle.bottom = -ARROW_HEIGHT;
          break;
        // Anchor is to the left → modal to the right
        case 'left':
          if (hasVerticalAlign) {
            rawModalStyle.left = left + (width / 2) - padding;
            rawArrowStyle.left = padding - (ARROW_WIDTH / 2);
          } else {
            rawModalStyle.left = right + padding;
            rawArrowStyle.borderRight = ARROW_BORDER;
            rawArrowStyle.left = -ARROW_HEIGHT;
          }
          break;
        // Anchor is to the right → modal to the left
        case 'right':
          if (hasVerticalAlign) {
            rawModalStyle.right = windowWidth - right + (width / 2) - padding;
            rawArrowStyle.right = padding - (ARROW_WIDTH / 2);
          } else {
            rawModalStyle.right = windowWidth - left + padding;
            rawArrowStyle.borderLeft = ARROW_BORDER;
            rawArrowStyle.right = -ARROW_HEIGHT;
          }
          break;
        // Vertically center modal on anchor
        case 'centerY':
          rawModalStyle.top = anchorCenterY;
          rawModalStyle.transform = 'translateY(-50%)';
          rawArrowStyle.top = '50%';
          rawArrowStyle.transform = 'translateY(-50%)';
          break;
        default:
          break;
      }
    });

    return { modalStyle: rawModalStyle, arrowStyle: rawArrowStyle };
  }, [isMobile, align, dims, padding]);

  return { anchorRef, modalStyle, arrowStyle };
};

export default useAnchoredModal;
